import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from './api';
import { logoSrc } from './LogoUploader';

const Center = ({ children }) => (
  <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-4">{children}</div>
);
const Section = ({ title, children }) => (
  <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
    <h2 className="text-white font-semibold mb-3">{title}</h2>
    <div className="space-y-2">{children}</div>
  </div>
);
const In = ({ label, value, onChange, type = 'text', required }) => (
  <div>
    <label className="block text-xs text-[#B0BEC5] mb-1">{label}</label>
    <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
  </div>
);
const ProductRow = ({ label, checked, seats, onCheck, onSeats }) => (
  <div className="flex items-center justify-between gap-3">
    <label className="flex items-center gap-2 text-sm text-white">
      <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} /> {label}
    </label>
    <div className="flex items-center gap-2">
      <span className="text-xs text-[#B0BEC5]">Seats</span>
      <input type="number" min="0" value={seats} disabled={!checked} onChange={(e) => onSeats(e.target.value)}
        className="w-20 px-2 py-1 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm disabled:opacity-40" />
    </div>
  </div>
);

// Public, association-branded brokerage signup page: /signup-brokerage/:slug
export default function BrokerageSignup() {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [notfound, setNotfound] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    name: '', office_address: '', city: '', state: '', zip: '', office_phone: '',
    license_number: '', mls_office_id: '', broker_name: '', broker_email: '', broker_phone: '',
    billing_name: '', billing_email: '', billing_address: '',
    want_re: false, seats_re: 1, want_cre: false, seats_cre: 1,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    fetch(`${API}/api/associations/public/signup/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((b) => {
        setBrand(b);
        setF((s) => ({ ...s, want_re: b.licensed_re && !b.licensed_cre, want_cre: b.licensed_cre && !b.licensed_re }));
      })
      .catch(() => setNotfound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (!(f.want_re || f.want_cre)) { setError('Select at least one product.'); return; }
    setSubmitting(true);
    try {
      const body = { ...f, seats_re: f.want_re ? Number(f.seats_re) || 0 : 0, seats_cre: f.want_cre ? Number(f.seats_cre) || 0 : 0 };
      const res = await fetch(`${API}/api/associations/public/signup/${slug}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setDone(true);
    } catch (err) { setError(err.message); } finally { setSubmitting(false); }
  };

  if (notfound) return <Center><p className="text-red-400">This signup link is not valid.</p></Center>;
  if (!brand) return <Center><p className="text-[#B0BEC5]">Loading…</p></Center>;
  if (done) return (
    <Center>
      <div className="text-center max-w-md">
        <p className="text-[#00E676] text-lg mb-2">Thank you — your brokerage has been submitted for approval.</p>
        <p className="text-[#B0BEC5]">{brand.display_name} will review it and send your principal broker an invitation to set a password.</p>
      </div>
    </Center>
  );

  return (
    <div className="min-h-screen bg-[#0A0E27] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          {brand.logo_url && <img src={logoSrc(brand.logo_url)} alt="" className="h-12 w-auto rounded" />}
          <div>
            <h1 className="text-white text-2xl font-bold">{brand.display_name}</h1>
            <p className="text-[#B0BEC5] text-sm">Brokerage signup</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <Section title="Brokerage">
            <In label="Brokerage name *" value={f.name} onChange={(v) => set('name', v)} required />
            <In label="Office address" value={f.office_address} onChange={(v) => set('office_address', v)} />
            <div className="grid grid-cols-3 gap-2">
              <In label="City" value={f.city} onChange={(v) => set('city', v)} />
              <In label="State" value={f.state} onChange={(v) => set('state', v)} />
              <In label="ZIP" value={f.zip} onChange={(v) => set('zip', v)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <In label="Office phone" value={f.office_phone} onChange={(v) => set('office_phone', v)} />
              <In label="Brokerage license #" value={f.license_number} onChange={(v) => set('license_number', v)} />
            </div>
            <In label="MLS office ID (optional)" value={f.mls_office_id} onChange={(v) => set('mls_office_id', v)} />
          </Section>
          <Section title="Principal broker">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <In label="Name *" value={f.broker_name} onChange={(v) => set('broker_name', v)} required />
              <In label="Email *" type="email" value={f.broker_email} onChange={(v) => set('broker_email', v)} required />
            </div>
            <In label="Phone" value={f.broker_phone} onChange={(v) => set('broker_phone', v)} />
          </Section>
          <Section title="Billing contact (optional — defaults to the principal broker)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <In label="Name" value={f.billing_name} onChange={(v) => set('billing_name', v)} />
              <In label="Email" type="email" value={f.billing_email} onChange={(v) => set('billing_email', v)} />
            </div>
            <In label="Mailing address" value={f.billing_address} onChange={(v) => set('billing_address', v)} />
          </Section>
          <Section title="Products & seats requested">
            {brand.licensed_re && <ProductRow label="Residential (RE)" checked={f.want_re} seats={f.seats_re} onCheck={(v) => set('want_re', v)} onSeats={(v) => set('seats_re', v)} />}
            {brand.licensed_cre && <ProductRow label="Commercial (CRE)" checked={f.want_cre} seats={f.seats_cre} onCheck={(v) => set('want_cre', v)} onSeats={(v) => set('seats_cre', v)} />}
          </Section>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button type="submit" disabled={submitting}
            className="w-full py-2.5 rounded-md bg-[#00D4FF] text-[#0A0E27] font-semibold disabled:opacity-50">
            {submitting ? 'Submitting…' : 'Submit for Approval'}
          </button>
          <p className="text-xs text-gray-500 text-center">Submitting creates no accounts — {brand.display_name} approves signups before anything is provisioned.</p>
        </form>
      </div>
    </div>
  );
}
